/**
 * Open an authenticated API byte stream (a citation's source document) in a
 * new tab.
 *
 * The content route needs the bearer token, so a plain link cannot open it.
 * Same mechanism as the Collections page's "open original"
 * (`downloadFile`): fetch with the token, then hand the browser a blob URL —
 * here navigated in a new tab, with the `#page=N` fragment so a PDF opens at
 * the cited page. The tab is opened synchronously (before any await) so popup
 * blockers treat it as a user gesture; if it is blocked anyway, the file is
 * downloaded instead.
 */

import { downloadFile } from "@/shared/api/integration-browse";

/** How long the blob URL stays valid after the tab has loaded it. */
const REVOKE_AFTER_MS = 60_000;

export async function openAuthenticatedInNewTab(
  getToken: () => Promise<string>,
  path: string,
  fragment = "",
  fallbackName?: string,
): Promise<void> {
  const tab = window.open("about:blank", "_blank");
  const token = await getToken();
  if (!tab) {
    await downloadFile(token, path, fallbackName);
    return;
  }
  try {
    const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`Could not open the document (HTTP ${res.status})`);
    const objectUrl = URL.createObjectURL(await res.blob());
    tab.location.href = `${objectUrl}${fragment}`;
    setTimeout(() => URL.revokeObjectURL(objectUrl), REVOKE_AFTER_MS);
  } catch (err) {
    tab.close();
    throw err;
  }
}
